import { useEffect, useMemo } from 'react';
import { Formik } from 'formik';

import {
  competitorSchema,
  manufacturerNameValidation,
  manufacturerSKUValidation,
  productNameValidation,
  VALIDATORS
} from './utils';
import { SearchFormProps } from '..';
import { ContainerForm } from './style';
import { FormikContainer } from '../styled';
import { ProductMatchValues } from '../../..';
import { SearchFormOptions } from '../../../enums';
import { SFormikTextfield } from '../../../../../sforms';
import { useCustomErrors } from '../../../../../../hooks';

export const formId = 'product-match-form';

interface CompetitorFormProps extends Omit<
  SearchFormProps,
  'disableButton' | 'urlRows' | 'textRows'
> {
  handleInputValidation: (invalid: boolean) => void;
}

const CompetitorForm = ({
  values,
  onSubmit,
  onChange,
  handleInputValidation
}: CompetitorFormProps) => {
  const { customErrors, customValidate } = useCustomErrors({
    schemas: competitorSchema,
    values: {
      [ProductMatchValues.ProductName]:
        values?.[ProductMatchValues.ProductName],
      [ProductMatchValues.ManufacturerName]:
        values?.[ProductMatchValues.ManufacturerName],
      [ProductMatchValues.ManufacturerSku]:
        values?.[ProductMatchValues.ManufacturerSku]
    }
  });

  const initialValues = useMemo(
    () => ({
      [ProductMatchValues.ProductName]:
        values?.[ProductMatchValues.ProductName],
      [ProductMatchValues.ManufacturerName]:
        values?.[ProductMatchValues.ManufacturerName],
      [ProductMatchValues.ManufacturerSku]:
        values?.[ProductMatchValues.ManufacturerSku]
    }),
    [values]
  );

  const isValid = useMemo(() => {
    if (
      !values?.[ProductMatchValues.ProductName] ||
      !values?.[ProductMatchValues.ManufacturerName] ||
      !values?.[ProductMatchValues.ManufacturerSku]
    )
      return false;

    if (
      // @ts-ignore
      customErrors?.manufacturerName ||
      // @ts-ignore
      customErrors?.manufacturerSku
    )
      return false;

    return true;
  }, [customErrors, values]);

  useEffect(() => {
    handleInputValidation?.(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  return (
    <FormikContainer>
      <Formik
        id={formId}
        onSubmit={() =>
          isValid &&
          onSubmit?.(SearchFormOptions.competitor, {
            value: values?.[ProductMatchValues.ManufacturerName],
            [ProductMatchValues.ProductName]:
              values?.[ProductMatchValues.ProductName],
            [ProductMatchValues.ManufacturerName]:
              values?.[ProductMatchValues.ManufacturerName],
            [ProductMatchValues.ManufacturerSku]:
              values?.[ProductMatchValues.ManufacturerSku]
          })
        }
        validateOnChange={false}
        enableReinitialize
        initialValues={initialValues}>
        <ContainerForm id={formId}>
          <SFormikTextfield
            name="manufacturerName"
            maxRows={4}
            label="Manufacturer Name*"
            placeholder="Enter a manufacturer name"
            maxLength={VALIDATORS.TEXT}
            onChange={(value: string) => {
              customValidate({
                schema: manufacturerNameValidation,
                value,
                name: 'manufacturerName'
              });
              onChange({ [ProductMatchValues.ManufacturerName]: value });
            }}
            customErrors={customErrors}
          />
          <SFormikTextfield
            name="manufacturerSku"
            maxRows={4}
            label="Manufacturer SKU*"
            placeholder="Enter manufacturer SKU"
            maxLength={VALIDATORS.TEXT}
            onChange={(value: string) => {
              customValidate({
                schema: manufacturerSKUValidation,
                value,
                name: 'manufacturerSku'
              });
              onChange({ [ProductMatchValues.ManufacturerSku]: value });
            }}
            customErrors={customErrors}
          />
          <SFormikTextfield
            name="productName"
            maxRows={4}
            label="Product Name*"
            placeholder="Enter a product name"
            maxLength={VALIDATORS.TEXT}
            onChange={(value: string) => {
              customValidate({
                schema: productNameValidation,
                value,
                name: 'productName'
              });
              onChange({ [ProductMatchValues.ProductName]: value });
            }}
            customErrors={customErrors}
          />
        </ContainerForm>
      </Formik>
    </FormikContainer>
  );
};

export default CompetitorForm;
